import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Tabs,
  Button,
  Space,
  List,
  Avatar,
  Divider,
  Typography,
  Badge,
} from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { SuppliersApi } from 'api/api';
import { SupplierDetailsDto, PaymentMethod } from 'api/models';
import { PageHeader } from 'components/page-headers/page-headers';
import { Main } from 'container/styled';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import { formatCurrency, formatDate } from 'utility/utility';
import { hasPermission } from 'utility/accessibility/hasPermission';
import { useMediaQuery } from 'react-responsive';
import { getPaymentMethodName } from 'constants/constants';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const SupplierDetailsPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState<SupplierDetailsDto | null>(null);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    fetchSupplierDetails();
  }, [id]);

  const fetchSupplierDetails = async () => {
    try {
      setLoading(true);
      const response = await new SuppliersApi().apiSuppliersIdGet({ id: Number(id) });
      setSupplier(response.data);
    } catch (error) {
      // Handle error
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = () => {
    navigate(`/supplier-payments/create?supplierId=${id}`);
  };

  if (loading || !supplier) {
    return <Card loading={true} />;
  }

  // Monthly spending chart data
  const monthlySpendingData = Object.entries(supplier.statistics?.monthlySpending || {}).map(([date, amount]) => ({
    month: dayjs(date).format('MMM YYYY'),
    amount: amount,
  }));

  // Project spending chart data
  const projectSpendingData = Object.entries(supplier.statistics?.projectSpending || {}).map(([projectId, amount]) => ({
    project: supplier.projectPayments?.find(p => p.projectId === Number(projectId))?.projectName || 'Unknown',
    amount: amount,
  }));

  // Colors for pie charts
  const COLORS = ['#52c41a', '#1890ff', '#ff4d4f'];

  // Custom tooltip formatter for currency
  const CurrencyTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ background: 'white', padding: '10px', border: '1px solid #ccc' }}>
          <p>{`${label}: ${formatCurrency(payload[0].value)}`}</p>
        </div>
      );
    }
    return null;
  };

  const renderPaymentItem = (item: any) => (
    <Card style={{ marginBottom: '12px' }} bordered={true}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>{item.description}</div>
          {item.projectName && <div style={{ fontSize: '14px', color: '#666' }}>{item.projectName}</div>}
        </div>
        <Tag color={item.isPaid ? 'green' : item.isPartiallyPaid ? 'blue' : item.isOverdue ? 'red' : 'orange'}>
          {item.isPaid
            ? t('suppliers:paid', 'Paid')
            : item.isPartiallyPaid
            ? t('suppliers:partially-paid', 'Partially Paid')
            : item.isOverdue
            ? t('suppliers:overdue', 'Overdue')
            : t('suppliers:pending', 'Pending')}
        </Tag>
      </div>

      <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '10px' }}>{formatCurrency(item.amount)}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px' }}>
        <div>
          <Text type="secondary">{t('suppliers:due', 'Due')}: </Text>
          <Text type={item.isOverdue && !item.isPaid ? 'danger' : undefined}>{formatDate(item.dueDate)}</Text>
        </div>
        {item.isPaid && (
          <div>
            <Text type="secondary">{t('suppliers:paid', 'Paid')}: </Text>
            <Text>{formatDate(item.paidDate!)}</Text>
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <>
      <PageHeader
        ghost
        title={supplier.name}
        buttons={[
          hasPermission('suppliers:manage-payments') && (
            <Button key="add" type="primary" onClick={handleAddPayment}>
              <FeatherIcon icon="plus" size={16} />
              {!isMobile && t('suppliers:add-payment', 'Add Payment')}
            </Button>
          ),
        ]}
      />

      <Main>
        <Row gutter={[16, 16]}>
          {/* Key Statistics */}
          <Col xs={24} lg={8}>
            <Card title={t('suppliers:key-stats', 'Key Statistics')}>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={12} lg={24}>
                  <Statistic
                    title={t('suppliers:total-payments', 'Total Payments')}
                    value={supplier.statistics?.totalPayments || 0}
                    prefix={<FeatherIcon icon="file-text" size={16} />}
                  />
                </Col>
                <Col xs={12} sm={12} lg={24}>
                  <Statistic
                    title={t('suppliers:total-paid', 'Total Paid')}
                    value={supplier.statistics?.totalPaid || 0}
                    prefix="€"
                    precision={2}
                    className="text-success"
                  />
                </Col>
                <Col xs={12} sm={12} lg={24}>
                  <Statistic
                    title={t('suppliers:pending-amount', 'Pending Amount')}
                    value={supplier.statistics?.totalPending || 0}
                    prefix="€"
                    precision={2}
                    className="text-warning"
                  />
                </Col>
                <Col xs={12} sm={12} lg={24}>
                  <Statistic
                    title={t('suppliers:overdue-amount', 'Overdue Amount')}
                    value={supplier.statistics?.overdueAmount || 0}
                    prefix="€"
                    precision={2}
                    className="text-danger"
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Payment Status */}
          <Col xs={24} lg={8}>
            <Card title={t('suppliers:payment-status', 'Payment Status')} style={{ height: '100%' }}>
              <div style={{ height: isMobile ? '250px' : '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        {
                          name: t('suppliers:paid', 'Paid'),
                          value:
                            (supplier.statistics?.totalPayments || 0) - (supplier.statistics?.pendingPayments || 0),
                        },
                        {
                          name: t('suppliers:pending', 'Pending'),
                          value:
                            (supplier.statistics?.pendingPayments || 0) - (supplier.statistics?.overduePayments || 0),
                        },
                        {
                          name: t('suppliers:overdue', 'Overdue'),
                          value: supplier.statistics?.overduePayments || 0,
                        },
                      ].filter(item => item.value > 0)}
                      cx="50%"
                      cy="50%"
                      labelLine={!isMobile}
                      outerRadius={isMobile ? 70 : 80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value, percent }) =>
                        isMobile
                          ? false // No labels on mobile
                          : value > 0
                          ? `${name} ${(percent * 100).toFixed(0)}%`
                          : ''
                      }
                      minAngle={15}
                    >
                      {COLORS.map((color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => (value > 0 ? value : '')} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>

          {/* Next Payments */}
          <Col xs={24} lg={8}>
            <Card title={t('suppliers:upcoming-payments', 'Upcoming Payments')}>
              {supplier.statistics?.nextPaymentDue && (
                <div className="next-payment-alert" style={{ marginBottom: '12px' }}>
                  <FeatherIcon icon="calendar" size={16} />
                  <span style={{ marginLeft: '8px' }}>
                    {t('suppliers:next-payment-due', 'Next payment due')}:{' '}
                    {formatDate(supplier.statistics?.nextPaymentDue)}
                  </span>
                </div>
              )}

              {isMobile ? (
                <List
                  dataSource={supplier.recentPayments?.filter(p => !p.isPaid).slice(0, 3) || []}
                  renderItem={renderPaymentItem}
                  locale={{ emptyText: 'No upcoming payments' }}
                />
              ) : (
                <Table
                  dataSource={supplier.recentPayments?.filter(p => !p.isPaid).slice(0, 5) || []}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: t('suppliers:due-date', 'Due Date'),
                      dataIndex: 'dueDate',
                      render: date => formatDate(date),
                    },
                    {
                      title: t('suppliers:amount', 'Amount'),
                      dataIndex: 'amount',
                      render: amount => formatCurrency(amount),
                    },
                    {
                      title: t('suppliers:paid-amount', 'Paid Amount'),
                      dataIndex: 'paidAmount',
                      render: paidAmount => formatCurrency(paidAmount),
                    },
                    {
                      title: t('suppliers:payment-method', 'Payment Method'),
                      dataIndex: 'paymentMethod',
                      render: paymentMethod =>
                        paymentMethod !== null && paymentMethod !== undefined
                          ? t(
                              `payment-methods:${getPaymentMethodName(paymentMethod).key}`,
                              getPaymentMethodName(paymentMethod).title,
                            )
                          : '-',
                    },
                    {
                      title: t('suppliers:status', 'Status'),
                      key: 'status',
                      render: (_, record) => (
                        <Tag
                          color={
                            record.isPaid
                              ? 'green'
                              : record.isPartiallyPaid
                              ? 'blue'
                              : record.isOverdue
                              ? 'red'
                              : 'orange'
                          }
                        >
                          {record.isPaid
                            ? t('suppliers:paid', 'Paid')
                            : record.isPartiallyPaid
                            ? t('suppliers:partially-paid', 'Partially Paid')
                            : record.isOverdue
                            ? t('suppliers:overdue', 'Overdue')
                            : t('suppliers:pending', 'Pending')}
                        </Tag>
                      ),
                    },
                  ]}
                />
              )}
            </Card>
          </Col>

          {/* Charts section - Tabs on mobile */}
          <Col xs={24}>
            {isMobile ? (
              <Card>
                <Tabs defaultActiveKey="monthly">
                  <TabPane tab={t('suppliers:monthly-spending', 'Monthly')} key="monthly">
                    <div style={{ height: '300px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={monthlySpendingData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis tickFormatter={value => formatCurrency(value)} />
                          <Tooltip content={<CurrencyTooltip />} />
                          <Line
                            type="monotone"
                            dataKey="amount"
                            stroke="#1890ff"
                            strokeWidth={2}
                            dot={{ fill: '#1890ff' }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </TabPane>

                  {projectSpendingData.length > 0 && (
                    <TabPane tab={t('suppliers:project-distribution', 'Projects')} key="projects">
                      <div style={{ height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={projectSpendingData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="amount"
                              nameKey="project"
                              label={false}
                            >
                              {projectSpendingData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={`hsl(${(index * 360) / projectSpendingData.length}, 70%, 50%)`}
                                />
                              ))}
                            </Pie>
                            <Tooltip formatter={value => formatCurrency(value as number)} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </TabPane>
                  )}
                </Tabs>
              </Card>
            ) : (
              <>
                {/* Monthly Spending Trend */}
                <Card title={t('suppliers:monthly-spending', 'Monthly Spending')} style={{ marginBottom: '24px' }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlySpendingData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={value => formatCurrency(value)} />
                      <Tooltip content={<CurrencyTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="amount"
                        stroke="#1890ff"
                        strokeWidth={2}
                        dot={{ fill: '#1890ff' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>

                {/* Project Distribution */}
                {projectSpendingData.length > 0 && (
                  <Card title={t('suppliers:project-distribution', 'Project Distribution')}>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={projectSpendingData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="amount"
                          nameKey="project"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {projectSpendingData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={`hsl(${(index * 360) / projectSpendingData.length}, 70%, 50%)`}
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={value => formatCurrency(value as number)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </>
            )}
          </Col>

          {/* Recent Payments */}
          <Col xs={24}>
            <Card title={t('suppliers:recent-payments', 'Recent Payments')}>
              {isMobile ? (
                <List
                  dataSource={supplier.recentPayments || []}
                  renderItem={renderPaymentItem}
                  pagination={{
                    pageSize: 5,
                    size: 'small',
                    simple: true,
                  }}
                  locale={{ emptyText: 'No payments found' }}
                />
              ) : (
                <Table
                  dataSource={supplier.recentPayments || []}
                  columns={[
                    {
                      title: t('suppliers:description', 'Description'),
                      dataIndex: 'description',
                    },
                    {
                      title: t('suppliers:project', 'Project'),
                      dataIndex: 'projectName',
                      render: text => text || '-',
                    },
                    {
                      title: t('suppliers:amount', 'Amount'),
                      dataIndex: 'amount',
                      render: amount => formatCurrency(amount),
                    },
                    {
                      title: t('suppliers:paid-amount', 'Paid Amount'),
                      dataIndex: 'paidAmount',
                      render: paidAmount => formatCurrency(paidAmount),
                    },
                    {
                      title: t('suppliers:payment-method', 'Payment Method'),
                      dataIndex: 'paymentMethod',
                      render: paymentMethod =>
                        paymentMethod !== null && paymentMethod !== undefined
                          ? t(
                              `payment-methods:${getPaymentMethodName(paymentMethod).key}`,
                              getPaymentMethodName(paymentMethod).title,
                            )
                          : '-',
                    },
                    {
                      title: t('suppliers:dates', 'Dates'),
                      key: 'dates',
                      render: (_, record) => (
                        <div>
                          <div>
                            <small>{t('suppliers:due', 'Due')}: </small>
                            <span className={record.isOverdue ? 'text-danger' : ''}>{formatDate(record.dueDate)}</span>
                          </div>
                          {(record.isPaid || record.isPartiallyPaid) && (
                            <div>
                              {record.isPartiallyPaid && (
                                <>
                                  <small>{t('suppliers:paid', 'Paid')}: </small>
                                  <small>{formatCurrency(record.paidAmount)}</small>
                                  <small className="text-secondary">
                                    {' '}
                                    ({t('suppliers:of', 'of')} {formatCurrency(record.amount)})
                                  </small>
                                </>
                              )}
                              {record.paidDate && (
                                <div>
                                  <small>{t('suppliers:paid', 'Paid')}: </small>
                                  <small>{formatDate(record.paidDate)}</small>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: t('suppliers:status', 'Status'),
                      key: 'status',
                      render: (_, record) => (
                        <Tag
                          color={
                            record.isPaid
                              ? 'green'
                              : record.isPartiallyPaid
                              ? 'blue'
                              : record.isOverdue
                              ? 'red'
                              : 'orange'
                          }
                        >
                          {record.isPaid
                            ? t('suppliers:paid', 'Paid')
                            : record.isPartiallyPaid
                            ? t('suppliers:partially-paid', 'Partially Paid')
                            : record.isOverdue
                            ? t('suppliers:overdue', 'Overdue')
                            : t('suppliers:pending', 'Pending')}
                        </Tag>
                      ),
                    },
                  ]}
                />
              )}
            </Card>
          </Col>
        </Row>
      </Main>

      {/* Floating action button for mobile */}
      {isMobile && hasPermission('suppliers:manage-payments') && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1000,
          }}
        >
          <Button
            type="primary"
            shape="circle"
            size="large"
            onClick={handleAddPayment}
            style={{ width: '50px', height: '50px', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}
          >
            <FeatherIcon icon="plus" size={20} />
          </Button>
        </div>
      )}
    </>
  );
};

export default SupplierDetailsPage;
